/*
 * mp3-quality.js — поведение общего выбора битрейта (<x-mp3-quality>).
 *
 * Зачем он есть. До 16.09.2026 каждый тул писал битрейт MP3 числом в коде:
 * перепись нашла двенадцать музыкальных тулов с константами 192/256, при 320 у
 * /music-mastering, и человек не мог ни узнать своё число, ни поменять его
 * (Pro-обращение 13.09: «мне при выходе нужен на трек отличный битрейт 320»).
 * Теперь ступени приходят из реестра, а выбор — сюда.
 *
 * Что модуль ДЕРЖИТ САМ:
 *   • память выбора между визитами (__toolSettings, ключ на тул);
 *   • честное значение: value() никогда не вернёт ступень, закрытую тиром, —
 *     даже если её кто-то «выбрал» в разметке или в сохранённых настройках;
 *   • дружбу с общим гейтом: quality-tier.js помечает закрытые чипы
 *     `data-qt-locked="1"` и сам кликает лучший доступный; мы это уважаем и
 *     не перетираем его решение своим сохранённым.
 *
 * ⚠️ Гейт приходит ПОЗЖЕ: тир доезжает событием `tier-bootstrap:ready`, а на
 * закэшированной странице — ещё позже. Поэтому активный чип перепроверяется по
 * этому событию и по MutationObserver на `data-qt-locked`, а не один раз при
 * старте. Без этого Премиум, чью страницу отдали из кэша гостю, увидел бы 128.
 */
(function () {
    'use strict';

    function boxOf(el) {
        if (!el) return null;
        return typeof el === 'string' ? document.getElementById(el) : el;
    }
    function chipsOf(box) {
        return Array.prototype.slice.call(box.querySelectorAll('[data-bitrate]'));
    }
    function isLocked(chip) {
        return chip.getAttribute('data-qt-locked') === '1' || chip.disabled === true;
    }
    function kbpsOf(chip) {
        return parseInt(chip.getAttribute('data-bitrate'), 10) || 0;
    }
    function topAllowed(box) {
        var best = null;
        chipsOf(box).forEach(function (c) {
            if (isLocked(c)) return;
            if (!best || kbpsOf(c) > kbpsOf(best)) best = c;
        });
        return best;
    }
    /** Гейт закрывает ступени только у блока, который его об этом просил. */
    function gated(box) {
        return !!box.querySelector('[data-quality-gate-group]');
    }
    /**
     * Лучшая ступень ПО ЗНАНИЮ О ТАРИФЕ, а не по расставленным замкам.
     *
     * ⚠️ При монтировании гейт ещё не отработал и замков нет — поднявшись на
     * верх лестницы «потому что ничего не закрыто», мы заставляли гейт тут же
     * понижать выбор и писать гостю «Качество снижено до 128» на пустом месте
     * (прод, 16.09.2026). Потолок тира известен синхронно (гостевой снимок
     * лежит в странице), поэтому спрашиваем его, а не замки.
     */
    /**
     * Потолок тира. ⚠️ Спрашивать ТОЛЬКО модуль гейта нельзя: он грузится
     * отдельным файлом и на момент нашего монтирования может ещё не выполниться
     * — «модуля нет» читалось как «ограничений нет», выбор прыгал на 320, и гейт
     * тут же его понижал с пометкой. Снимок тира лежит в самой странице
     * (`__tierLimits`, гостевой вариант печатает layout), его и читаем.
     */
    function capNow() {
        try {
            if (window.__qualityTier && window.__qualityTier.audioMaxKbps) {
                var c = window.__qualityTier.audioMaxKbps();
                if (isFinite(c) && c > 0) return c;
            }
            var q = window.__tierLimits && window.__tierLimits.quality;
            if (q && typeof q.audio_max_kbps === 'number' && q.audio_max_kbps > 0) return q.audio_max_kbps;
        } catch (_) {}
        return Infinity;
    }
    function bestByCap(box) {
        if (!gated(box)) return topAllowed(box);   // лейн оплачен — потолка нет
        var cap = capNow();
        // Потолок ещё неизвестен — НЕ поднимаем: лучше остаться на нижней
        // ступени разметки и подняться на `tier-bootstrap:ready`, чем показать
        // человеку выбор, который у него сейчас же отберут.
        if (!isFinite(cap)) return activeChip(box);
        var best = null;
        chipsOf(box).forEach(function (c) {
            if (isLocked(c) || kbpsOf(c) > cap) return;
            if (!best || kbpsOf(c) > kbpsOf(best)) best = c;
        });
        return best || topAllowed(box);
    }
    function activeChip(box) {
        var a = box.querySelector('[data-bitrate].active');
        return a && !isLocked(a) ? a : null;
    }

    function paint(box, chip) {
        chipsOf(box).forEach(function (c) {
            var on = c === chip;
            c.classList.toggle('active', on);
            c.setAttribute('aria-checked', on ? 'true' : 'false');
        });
    }

    function slugOf(box) {
        return box.getAttribute('data-mq-slug') || (window.__TOOL_SLUG || 'unknown');
    }

    function remember(box, kbps) {
        try {
            if (window.__toolSettings && window.__toolSettings.save) {
                window.__toolSettings.save(slugOf(box), 'mp3_kbps', String(kbps));
            }
        } catch (_) {}
    }

    /** Лучший доступный чип: сохранённый выбор, если он открыт, иначе верх лестницы. */
    function settle(box) {
        var saved = 0;
        try {
            if (window.__toolSettings && window.__toolSettings.load) {
                saved = parseInt(window.__toolSettings.load(slugOf(box), 'mp3_kbps', 0), 10) || 0;
            }
        } catch (_) {}

        /* ⚠️ Сохранённый выбор ставим ДАЖЕ ЕСЛИ он закрыт — специально.
         *
         * Человек однажды выбрал 320 Премиумом, тариф кончился: молча съехать на
         * 128 значит отнять выбранное без единого слова. Пусть закрытая ступень
         * окажется активной — общий гейт увидит это, опустит на лучшую доступную
         * и САМ объяснит это своими словами («Качество снижено до 128…»).
         *
         * А вот когда человек не выбирал ничего, активной должна стать лучшая
         * ДОСТУПНАЯ, а не верх лестницы: иначе гость, который ничего не терял,
         * читает «качество снижено» на пустом месте (так и было на проде
         * 16.09.2026 — у соседних тулов с тем же гейтом такой пометки нет). */
        var chips = chipsOf(box), pick = null;
        if (saved) {
            chips.forEach(function (c) { if (kbpsOf(c) === saved) pick = c; });
        }
        if (!pick) pick = bestByCap(box) || activeChip(box) || chips[0] || null;
        if (pick) paint(box, pick);
        plan(box);
        return pick;
    }

    /**
     * Фраза «ваш тариф — до N · Премиум — до 320» со ссылкой, ПРЯМО ПОД
     * переключателем.
     *
     * Общий гейт по умолчанию публикует её в блок лимитов страницы — правильный
     * единственный дом для подробного чтения, но по нашему же замеру его на
     * длинных страницах не видят (99.2 % показов seen=0). Решение принимается
     * здесь, значит и объяснение должно стоять здесь: у модуля для этого есть
     * режим inline. Премиуму строка не рисуется вовсе — renderPlanLine выходит,
     * когда потолок тарифа равен верху лестницы.
     */
    function plan(box) {
        try {
            if (window.__qualityTier && window.__qualityTier.planLine) {
                window.__qualityTier.planLine(box, 'kbps', { inline: true });
            }
        } catch (_) {}
    }

    /**
     * У тула может быть НЕСКОЛЬКО мест выдачи (у /kalimba-online их два —
     * «Секвенция» и «Луп»), и качество там одно. Без этого человек менял бы
     * ступень в одной панели, а вторая молча оставалась на прежней.
     */
    function sync(box, kbps) {
        var slug = slugOf(box);
        Array.prototype.slice.call(document.querySelectorAll('[data-mq-slug="' + slug + '"]')).forEach(function (other) {
            if (other === box) return;
            var twin = other.querySelector('[data-bitrate="' + kbps + '"]');
            if (twin && !isLocked(twin)) paint(other, twin);
        });
    }

    function mount(box) {
        box = boxOf(box);
        if (!box || box.__mqMounted) return;
        box.__mqMounted = true;

        chipsOf(box).forEach(function (chip) {
            chip.addEventListener('click', function (ev) {
                if (isLocked(chip)) return;       // закрытую ступень продаёт гейт, не мы
                paint(box, chip);
                /* ⚠️ Гейт опускает выбор СИНТЕТИЧЕСКИМ кликом по лучшей доступной
                 * ступени. Записать его как выбор человека — значит стереть то,
                 * что он выбрал сам: вернётся Премиум, а его 320 уже нет. */
                if (!ev || ev.isTrusted !== false) { box.__mqTouched = true; remember(box, kbpsOf(chip)); }
                sync(box, kbpsOf(chip));
                try {
                    window.__ae && window.__ae('feature_use', {
                        slug: slugOf(box), f: 'mp3_quality', kbps: kbpsOf(chip),
                    });
                } catch (_) {}
                box.dispatchEvent(new CustomEvent('mp3-quality:change', { bubbles: true, detail: { kbps: kbpsOf(chip) } }));
            });
        });

        settle(box);

        /* Гейт доезжает ПОЗЖЕ монтирования: пока тир неизвестен, ступени не
         * закрыты и фразы про Премиум быть не может (renderPlanLine выходит на
         * неизвестном потолке). Поэтому и выбор, и фразу пересогласуем на каждое
         * изменение замков, а не один раз при старте — иначе человек видит
         * закрытые ступени и ни слова о том, что их открывает. */
        try {
            var mo = new MutationObserver(function () {
                plan(box);
                if (!activeChip(box)) settle(box);
            });
            mo.observe(box, { subtree: true, attributes: true, attributeFilter: ['data-qt-locked', 'disabled'] });
        } catch (_) {}
        // Поздний проход: на закэшированной странице событие тира может не
        // прийти вовсе, а замки поставит аварийный гостевой снимок гейта.
        setTimeout(function () { plan(box); if (!activeChip(box)) settle(box); }, 2000);
    }

    function mountAll() {
        Array.prototype.slice.call(document.querySelectorAll('[data-mq-slug]')).forEach(mount);
    }

    /**
     * Битрейт, который тул обязан отдать энкодеру. Всегда число из лестницы и
     * никогда выше доступного этому человеку; без блока — верх реестра
     * (fail-open: отсутствие выбора не должно ронять экспорт).
     */
    function value(el, fallback) {
        var box = boxOf(el);
        if (!box) return fallback || 320;
        var chip = activeChip(box) || topAllowed(box);
        return chip ? kbpsOf(chip) : (parseInt(box.getAttribute('data-mq-top'), 10) || fallback || 320);
    }

    window.__mp3Quality = { mount: mount, mountAll: mountAll, value: value, settle: settle };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', mountAll);
    } else {
        mountAll();
    }
    /* Тариф доезжает событием: до него у вошедшего Премиума в странице лежит
     * ГОСТЕВОЙ снимок потолка, и выбор стоит на 128. Пересогласуемся — но
     * только пока человек не выбрал сам: его клик сильнее нашего умолчания. */
    document.addEventListener('tier-bootstrap:ready', function () {
        Array.prototype.slice.call(document.querySelectorAll('[data-mq-slug]')).forEach(function (box) {
            plan(box);
            if (!box.__mqTouched) settle(box);
        });
    });
})();
